import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { DiscountTooltip } from '@/components/DiscountTooltip/DiscountTooltip'
import { Text } from 'ui/src/components/Text/Text'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { BUNDLE_DISCOUNT_PERCENTAGE } from '../../bundleDiscount.constants'
import { discountStatus, segment, segmentActive, segmentAnimated, segments } from './BundleDiscountProgress.css'

type BundleDiscountProgressProps = {
  eligibleInsuranceCount: number
  showStatus?: boolean
  shouldAnimate?: boolean
}

export function BundleDiscountProgress({
  eligibleInsuranceCount,
  showStatus = false,
  shouldAnimate = true,
}: BundleDiscountProgressProps) {
  const { t } = useTranslation()
  // If shouldAnimate is false, the last segment should be active and not animated
  const isActiveAt = (threshold: number) => eligibleInsuranceCount >= threshold + (shouldAnimate ? 1 : 0)

  return (
    <div className={yStack({ gap: 'xs' })}>
      {/* Progress Bar */}
      <div className={segments}>
        {/* Segment 1: 1 insurance */}
        <ProgressSegment
          isActive={isActiveAt(1)}
          isAnimated={shouldAnimate && eligibleInsuranceCount === 1}
          title={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_TITLE_ONE_INSURANCE')}
          subtitle={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_SUBTITLE_NO_DISCOUNT')}
        />

        {/* Segment 2: 2 insurances */}
        <ProgressSegment
          isActive={isActiveAt(2)}
          isAnimated={shouldAnimate && eligibleInsuranceCount === 2}
          title={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_TITLE_TWO_INSURANCES')}
          subtitle={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_SUBTITLE_APPLIED_DISCOUNT', {
            percentage: BUNDLE_DISCOUNT_PERCENTAGE,
          })}
        />

        {/* Segment 3: 3+ insurances */}
        <ProgressSegment
          isActive={isActiveAt(3)}
          isAnimated={shouldAnimate && eligibleInsuranceCount === 3}
          title={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_TITLE_THREE_OR_MORE')}
          subtitle={t('BUNDLE_DISCOUNT_PROGRESS_SEGMENT_SUBTITLE_APPLIED_DISCOUNT', {
            percentage: BUNDLE_DISCOUNT_PERCENTAGE,
          })}
        />
      </div>

      {/* Status Display */}
      {showStatus && (
        <div className={xStack({ justifyContent: 'center' })}>
          <DiscountTooltip
            className={discountStatus}
            color="green"
            orientation="bottom"
            subtitle={
              eligibleInsuranceCount >= 2
                ? t('BUNDLE_DISCOUNT_PROGRESS_STATUS_ELIGIBLE')
                : t('BUNDLE_DISCOUNT_PROGRESS_STATUS_INELIGIBLE')
            }
          />
        </div>
      )}
    </div>
  )
}

type ProgressSegmentProps = {
  isActive: boolean
  isAnimated: boolean
  title: string
  subtitle: string
}

function ProgressSegment({ isActive, isAnimated, title, subtitle }: ProgressSegmentProps) {
  return (
    <div className={yStack({ gap: 'xs', alignItems: 'center' })}>
      <div className={clsx(segment, isActive && segmentActive, isAnimated && segmentAnimated)} />
      <div className={yStack({ alignItems: 'center' })}>
        <Text as="span" size="xs" color="textPrimary">
          {title}
        </Text>
        <Text as="span" size="xs" color="textSecondary">
          {subtitle}
        </Text>
      </div>
    </div>
  )
}
